import { TRANSFER_POLICY_CAP_TYPE, TRANSFER_POLICY_CREATED_EVENT, TRANSFER_POLICY_TYPE } from "../types/transfer-policy.mjs";
import { queryEvents } from "./client-utils.mjs";
import { TransferPolicy, TransferPolicyCap } from "../contracts/0x2/transfer_policy.mjs";
import { isValidSafeAddress } from "@safecoin/safe/utils";
//#region src/query/transfer-policy.ts
/**
* Searches the `TransferPolicy`-s for the given type. The search is performed via
* the `TransferPolicyCreated` event. The policy can either be owned or shared,
* and the caller needs to filter the results accordingly (ie single owner can not
* be accessed by anyone but the owner).
*
* @param client - A Safe client with the Core API
* @param type - The type of the asset (e.g., "0x123::nft::NFT")
*/
async function queryTransferPolicy(client, type) {
	const search = (await queryEvents(client, `${TRANSFER_POLICY_CREATED_EVENT}<${type}>`)).map((event) => event.json);
	const { objects } = await client.core.getObjects({
		objectIds: search.map((policy) => policy.id),
		include: { content: true }
	});
	return objects.filter((obj) => !(obj instanceof Error) && obj.content).map((obj) => {
		if (obj instanceof Error) throw obj;
		if (!obj.content) throw new Error(`Invalid policy: ${obj.objectId}, expected object with content`);
		const parsed = TransferPolicy.parse(obj.content);
		return {
			id: obj.objectId,
			type: `${TRANSFER_POLICY_TYPE}<${type}>`,
			owner: obj.owner,
			rules: parsed.rules.contents.map((rule) => rule.name),
			balance: parsed.balance.value.toString()
		};
	});
}
/**
* Fetches all TransferPolicyCap objects owned by an address for a specific type.
*
* @param client - The Safe client
* @param address - The owner address
* @param type - The type of the asset
* @returns Array of TransferPolicyCap objects
*/
async function queryTransferPolicyCapsByType(client, address, type) {
	if (!isValidSafeAddress(address)) return [];
	const policies = [];
	let hasNextPage = true;
	let cursor = null;
	while (hasNextPage) {
		const result = await client.core.listOwnedObjects({
			owner: address,
			type: `${TRANSFER_POLICY_CAP_TYPE}<${type}>`,
			cursor,
			limit: 50,
			include: { content: true }
		});
		for (const obj of result.objects) if (obj.content) policies.push({
			policyId: TransferPolicyCap.parse(obj.content).policy_id,
			policyCapId: obj.objectId,
			type
		});
		hasNextPage = result.hasNextPage;
		cursor = result.cursor;
	}
	return policies;
}
/**
* Fetches all TransferPolicyCap objects owned by an address (all types).
*
* Uses struct-level filtering (without type parameters) to efficiently query
* only TransferPolicyCap objects. This matches all generic instantiations
* (e.g., TransferPolicyCap<T> for any T) and is supported natively by all clients.
*
* @param client - The Safe client
* @param address - The owner address
* @returns Array of TransferPolicyCap objects or undefined if address is invalid
*/
async function queryOwnedTransferPolicies(client, address) {
	if (!isValidSafeAddress(address)) return;
	let hasNextPage = true;
	let cursor = null;
	const policies = [];
	while (hasNextPage) {
		const result = await client.core.listOwnedObjects({
			owner: address,
			type: TRANSFER_POLICY_CAP_TYPE,
			cursor,
			limit: 50,
			include: { content: true }
		});
		for (const obj of result.objects) if (obj.content) {
			const objectType = obj.type.replace(`${TRANSFER_POLICY_CAP_TYPE}<`, "").slice(0, -1);
			policies.push({
				policyId: TransferPolicyCap.parse(obj.content).policy_id,
				policyCapId: obj.objectId,
				type: objectType
			});
		}
		hasNextPage = result.hasNextPage;
		cursor = result.cursor;
	}
	return policies;
}
//#endregion
export { queryOwnedTransferPolicies, queryTransferPolicy, queryTransferPolicyCapsByType };

//# sourceMappingURL=transfer-policy.mjs.map